import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';

const ZimmetListesiPage = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toasts, addToast, removeToast } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const searchTimeoutRef = useRef(null);

  // API çağrıları
  const fetchInventory = useCallback(async (search, category, status) => {
    try {
      setLoading(true);
      const params = {};
      if (search && search.trim()) params.search = search.trim();
      if (category) params.category = category;
      if (status) params.status = status;
      
      const response = await api.getInventory(params);
      if (response.success) {
        setInventoryItems(response.inventory);
      }
    } catch (error) {
      console.error('Zimmet listesi yüklenirken hata:', error);
      addToast('Zimmet listesi yüklenirken bir hata oluştu', 'error', 5000);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  // Search term değiştiğinde debounced search
  useEffect(() => {
    // Önceki timeout'u temizle
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Yeni timeout oluştur
    searchTimeoutRef.current = setTimeout(() => {
      fetchInventory(searchTerm, filterCategory, filterStatus);
    }, 500);
    
    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, filterCategory, filterStatus, fetchInventory]);


  // Filtreleri sıfırla
  const resetFilters = () => {
    setSearchTerm('');
    setFilterCategory('');
    setFilterStatus('');
    // Filtreleri sıfırladıktan sonra tüm veriyi çek
    fetchInventory('', '', '');
  };

  // Arama terimi değişikliği
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDeleteItem = async (id) => {
    if (window.confirm('Bu zimmet kaydını silmek istediğinizden emin misiniz?')) {
      try {
        const response = await api.deleteInventoryItem(id);
        if (response.success) {
          addToast('Zimmet kaydı başarıyla silindi', 'success', 3000);
          fetchInventory();
        }
      } catch (error) {
        console.error('Zimmet kaydı silinirken hata:', error);
        addToast('Zimmet kaydı silinirken bir hata oluştu', 'error', 5000);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Aktif':
        return 'bg-success';
      case 'Pasif':
        return 'bg-danger';
      case 'Bakımda':
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  };

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
          <div className="modern-hr-header-content">
            <div className="modern-hr-header-text">
              <h1>Zimmet Listesi</h1>
            </div>
            <div className="modern-hr-header-actions">
              <div className="modern-hr-stats">
                <div className="modern-hr-stat-item">
                  <i className="bx bx-package"></i>
                  <span>{inventoryItems.length} Zimmet</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modern-hr-widget">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-package"></i>
              <h4>Zimmet Listesi</h4>
            </div>
            <div className="modern-hr-widget-actions">
              <div className="modern-hr-filters">
                <div className="modern-hr-filter-group">
                  <div className="modern-hr-filter-item">
                    <i className="bx bx-search"></i>
                    <input 
                      type="text" 
                      className="modern-hr-form-input" 
                      placeholder="İsim, seri no veya zimmet alan kişi ara..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <div className="modern-hr-filter-item">
                    <i className="bx bx-category"></i>
                    <select 
                      className="modern-hr-form-select"
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                    >
                      <option value="">Tüm Kategoriler</option>
                      <option value="Bilgisayar">Bilgisayar</option>
                      <option value="Telefon">Telefon</option>
                      <option value="Monitör">Monitör</option>
                      <option value="Yazıcı">Yazıcı</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>
                  <div className="modern-hr-filter-item">
                    <i className="bx bx-check-circle"></i>
                    <select 
                      className="modern-hr-form-select"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="">Tüm Durumlar</option>
                      <option value="Aktif">Aktif</option>
                      <option value="Pasif">Pasif</option>
                      <option value="Bakımda">Bakımda</option>
                    </select>
                  </div>
                  <div className="modern-hr-filter-item">
                    <button 
                      className="modern-hr-btn modern-hr-btn-outline modern-hr-btn-sm"
                      onClick={resetFilters}
                      title="Filtreleri Sıfırla"
                    >
                      <i className="bx bx-x"></i>
                      Temizle
                    </button>
                  </div>
                </div>
                {(searchTerm || filterCategory || filterStatus) && (
                  <div className="modern-hr-active-filters">
                    <span className="modern-hr-filter-label">Aktif Filtreler:</span>
                    {searchTerm && (
                      <span className="modern-hr-filter-tag">
                        <i className="bx bx-search"></i>
                        Arama: "{searchTerm}"
                        <button 
                          onClick={() => setSearchTerm('')}
                          className="modern-hr-filter-remove"
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      </span>
                    )}
                    {filterCategory && (
                      <span className="modern-hr-filter-tag">
                        <i className="bx bx-category"></i>
                        Kategori: {filterCategory}
                        <button 
                          onClick={() => setFilterCategory('')}
                          className="modern-hr-filter-remove"
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      </span>
                    )}
                    {filterStatus && (
                      <span className="modern-hr-filter-tag">
                        <i className="bx bx-check-circle"></i>
                        Durum: {filterStatus}
                        <button 
                          onClick={() => setFilterStatus('')}
                          className="modern-hr-filter-remove"
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="modern-hr-widget-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <p className="mt-3">Zimmet listesi yükleniyor...</p>
              </div>
            ) : inventoryItems.length === 0 ? (
              <div className="text-center py-5">
                <i className="bx bx-package bx-lg text-muted"></i>
                <p className="mt-3">Henüz zimmet kaydı bulunmamaktadır.</p>
              </div>
            ) : (
              <div className="modern-inventory-list">
                {inventoryItems.map((item) => (
                  <div key={item._id} className="modern-inventory-item">
                    <div className="modern-inventory-item-header">
                      <div className="modern-inventory-item-title">
                        <div className="modern-inventory-item-icon">
                          <i className="bx bx-laptop"></i>
                        </div>
                        <div>
                          <h6>{item.name}</h6>
                          <p>{item.category} • {item.serialNumber}</p>
                        </div>
                      </div>
                      <div className="modern-inventory-item-status">
                        <span className={`badge ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                    <div className="modern-inventory-item-body">
                      <div className="modern-inventory-item-info">
                        <div className="modern-inventory-info-item">
                          <i className="bx bx-user"></i>
                          <span>{item.assignedTo}</span>
                        </div>
                        <div className="modern-inventory-info-item">
                          <i className="bx bx-building"></i>
                          <span>{item.department}</span>
                        </div>
                        <div className="modern-inventory-info-item">
                          <i className="bx bx-map"></i>
                          <span>{item.location}</span>
                        </div>
                        <div className="modern-inventory-info-item">
                          <i className="bx bx-calendar"></i>
                          <span>{new Date(item.dateAssigned).toLocaleDateString('tr-TR')}</span>
                        </div>
                      </div>
                      {item.notes && (
                        <div className="modern-inventory-item-notes">
                          <p>{item.notes}</p>
                        </div>
                      )}
                    </div>
                    <div className="modern-inventory-item-footer">
                      <div className="modern-inventory-item-actions">
                        <button className="modern-hr-btn modern-hr-btn-sm modern-hr-btn-outline">
                          <i className="bx bx-edit"></i>
                        </button>
                        <button className="modern-hr-btn modern-hr-btn-sm modern-hr-btn-outline">
                          <i className="bx bx-transfer"></i>
                        </button>
                        <button 
                          className="modern-hr-btn modern-hr-btn-sm modern-hr-btn-danger"
                          onClick={() => handleDeleteItem(item._id)}
                        >
                          <i className="bx bx-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default ZimmetListesiPage;
